import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  Avatar,
  FormErrorMessage,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    fullName: user?.fullName || '',
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (password && password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (password && password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    // In a real app, this would be an API call to update the user profile
    // For now, we'll simulate a successful update
    setTimeout(() => {
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false);
      
      // Clear password fields after successful update
      setPassword('');
      setConfirmPassword('');
    }, 1000);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast({
      title: 'Logged out',
      description: 'You have been successfully logged out.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Container maxW="container.md" py={8}>
      <Heading mb={6}>Your Profile</Heading>
      
      <Flex direction={{ base: 'column', md: 'row' }} gap={8}>
        <Box flex="1" bg={bgColor} p={6} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <Flex direction="column" align="center" mb={6}>
            <Avatar 
              size="2xl" 
              name={formData.fullName || formData.username} 
              src="" 
              mb={4}
              bg="brand.500"
              color="white"
            />
            <Heading size="md">{formData.fullName || formData.username}</Heading>
            <Text color="gray.500">{formData.email}</Text>
          </Flex>
          
          <Stack spacing={4}>
            <Button 
              colorScheme="brand" 
              variant="outline" 
              w="full"
              onClick={() => navigate('/dashboard/packages')}
            >
              Manage Your Packages
            </Button>
            
            <Button 
              colorScheme="red" 
              variant="outline" 
              w="full"
              onClick={onOpen}
            >
              Log Out
            </Button>
          </Stack>
        </Box>
        
        <Box 
          flex="2" 
          as="form" 
          onSubmit={handleSubmit}
          bg={bgColor} 
          p={6} 
          borderRadius="lg" 
          boxShadow="sm" 
          borderWidth="1px" 
          borderColor={borderColor}
        >
          <Heading size="md" mb={4}>Edit Profile</Heading>
          
          <Stack spacing={4}>
            <FormControl id="username">
              <FormLabel>Username</FormLabel>
              <Input 
                name="username"
                value={formData.username}
                onChange={handleChange}
                isReadOnly
                bg="gray.50"
                _dark={{ bg: 'gray.700' }}
              />
              <Text fontSize="sm" color="gray.500">Username cannot be changed</Text>
            </FormControl>
            
            <FormControl id="fullName">
              <FormLabel>Full Name</FormLabel>
              <Input 
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Your full name"
              />
            </FormControl>
            
            <FormControl id="email" isInvalid={!!errors.email}>
              <FormLabel>Email</FormLabel>
              <Input 
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your email address"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
            
            <Divider my={2} />
            
            <Heading size="sm" mb={2}>Change Password</Heading>
            
            <FormControl id="password" isInvalid={!!errors.password}>
              <FormLabel>New Password</FormLabel>
              <Input 
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
              />
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>
            
            <FormControl id="confirmPassword" isInvalid={!!errors.confirmPassword}>
              <FormLabel>Confirm New Password</FormLabel>
              <Input 
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
              <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
            </FormControl>
            
            <Button 
              mt={4} 
              colorScheme="brand" 
              type="submit" 
              isLoading={isLoading}
            >
              Save Changes
            </Button>
          </Stack>
        </Box>
      </Flex>
      
      {/* Logout confirmation dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Log Out
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to log out?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleLogout} ml={3}>
                Log Out
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}
